import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Center, Flex, Link, Text } from '@chakra-ui/react';

import type { ApiAcceptedLanguage } from '@izimi/api';

import { ErrorIllustration } from './ErrorIllustration';

const helpUrls: Record<ApiAcceptedLanguage, string> = {
  nl: 'https://www.izimi.be/nl/faq',
  fr: 'https://www.izimi.be/fr/faq',
  de: 'https://www.izimi.be/de/faq',
  en: 'https://www.izimi.be/en/faq',
} as const;

type WithButtonProps = {
  withButton: true;
  buttonLabel?: string;
  buttonDestination?: string;
  buttonOnClick?: () => void;
};

type WithoutButtonProps = {
  withButton?: false;
  buttonLabel?: never;
  buttonDestination?: never;
  buttonOnClick?: never;
};

type Props = {
  statusCode?: number;
  title?: string;
  message?: string | ReactNode;
  illustration?: ReactNode;
  replace?: boolean;
} & (WithButtonProps | WithoutButtonProps);

const ErrorPage = ({
  statusCode,
  title,
  message,
  illustration,
  replace,
  withButton,
  buttonDestination,
  buttonLabel,
  buttonOnClick,
}: Props) => {
  const { t, i18n } = useTranslation('errors');

  const handleReload = () => {
    buttonOnClick?.();
    if (buttonDestination) {
      if (replace) {
        window.location.replace(buttonDestination);
        return;
      }
      window.location.assign(buttonDestination);
    } else {
      window.location.reload();
    }
  };

  return (
    <Center flexDir='column' gap={2} position='relative' height='100%'>
      {illustration ?? <ErrorIllustration />}
      {!!statusCode && (
        <Text variant='heading5' color='neutral400'>
          {statusCode}
        </Text>
      )}
      <Text as='h1' variant='heading1'>
        {title ?? t(`error${statusCode}.title`)}
      </Text>
      <Text as='h3' variant='paragraph'>
        {message ?? t(`error${statusCode}.message`)}
      </Text>
      <Flex flexDir='column' alignItems='center' gap={2} mt={4}>
        {withButton && (
          <Button variant='primary500' onClick={handleReload}>
            {buttonLabel ?? t('shared:action.reload')}
          </Button>
        )}
        <Link href={helpUrls[i18n.language]} variant='heading6' isExternal>
          {t('shared:navigation.help')}
        </Link>
      </Flex>
    </Center>
  );
};

export default ErrorPage;
